import {
  Conversation,
  ConversationDirection,
  ConversationsChannels,
  MetaConversation,
} from 'lib-shared-legacy-types';
import { User } from 'lib-shared-types-api';

import { PHONE_BASED_CHANNELS } from './channels';
import { numberFormatterInternational } from './telephonyHelpers';

const TITLE_MAP = {
  widgetchat: 'Chat with',
  messenger: 'Messaging with',
  pstnphone: {
    inbound: 'Inbound call from',
    outbound: 'Outbound call to',
    internal: 'Internal call with',
    default: 'Call',
  },
  email: {
    inbound: 'Inbound email from',
    outbound: 'Outbound email to',
    default: 'Email',
  },
  contactform: 'Contact form submission by',
  voicemail: 'Voicemail from',
  callback: 'Callback request from',
  facebookmessenger: 'Messaging with',
  whatsapp: 'Messaging with',
};

export function getTranscriptDateLead(
  date: Date,
  contentType: 'html' | 'markdown',
): string {
  if (!date || date instanceof Date === false) {
    return '';
  }

  const italicStart = contentType === 'html' ? '<i>' : '';
  const italicEnd = contentType === 'html' ? '</i>' : '';

  let dateString: string;

  try {
    dateString = date.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    });
  } catch (e) {
    dateString = `${date.toDateString()}`;
    // eslint-disable-next-line no-console
    console.warn('toLocaleDateString exception', e);
  }

  return `, ${italicStart}${dateString}${italicEnd}:`;
}

function getRequesterName(
  channel: Conversation['initial_channel'],
  requester: User | null,
): string {
  if (!requester) return 'Anonymous';
  const sender = requester.name;
  if (!sender) {
    if (
      PHONE_BASED_CHANNELS.includes(channel as ConversationsChannels) &&
      requester.phone_number
    ) {
      return numberFormatterInternational({
        phoneNumber: requester.phone_number,
      });
    }
    if (requester.email) {
      return requester.email;
    }
    return 'Anonymous';
  }

  return sender;
}

export function getSubject(
  attributes: Conversation['attributes'] | null,
): string | null {
  const subject = (attributes || {}).subject;
  if (subject && typeof subject === 'string' && subject.trim().length > 0) {
    return subject;
  }
  return null;
}

type HumanConversationChannel =
  | 'Chat'
  | 'Phone'
  | 'Email'
  | 'Contact form'
  | 'Voicemail'
  | 'Callback';

export function getHumanConversationChannel(
  conversation: Conversation,
): HumanConversationChannel | null {
  let conversationType: HumanConversationChannel | null = null;
  if (conversation.initial_channel) {
    if (conversation.initial_channel === 'widgetchat') {
      conversationType = 'Chat';
    } else if (conversation.initial_channel === 'pstnphone') {
      conversationType = 'Phone';
    } else if (conversation.initial_channel === 'email') {
      conversationType = 'Email';
    } else if (conversation.initial_channel === 'contactform') {
      conversationType = 'Contact form';
    } else if (conversation.initial_channel === 'voicemail') {
      conversationType = 'Voicemail';
    } else if (conversation.initial_channel === 'callback') {
      conversationType = 'Callback';
    }
  }
  return conversationType;
}

export function getConversationTitle({
  conversation,
  requester,
}: Pick<MetaConversation, 'requester'> & {
  conversation: Pick<
    MetaConversation['conversation'],
    'initial_channel' | 'direction' | 'attributes'
  >;
}): string | undefined {
  const { initial_channel, direction, attributes } = conversation;

  if (
    ['email', 'contactform'].includes(initial_channel as ConversationsChannels)
  ) {
    const subject = getSubject(attributes);
    if (subject) return subject;
  }

  const title = TITLE_MAP[initial_channel as ConversationsChannels];

  if (title) {
    const name = getRequesterName(
      conversation.initial_channel,
      requester as User,
    );

    if (typeof title === 'string') {
      return `${title} ${name}`;
    }
    const directionTitle = title[direction as ConversationDirection];
    if (directionTitle) {
      return `${directionTitle} ${name}`;
    }
    return `${title.default} ${name}`;
  }

  return undefined;
}
